import { TYPES } from '@/data/constants/product';
import { ProductType } from '@/data/types/Product';
import { PROMOTION_TYPES } from 'integration/data/custom/constants/promoIconConstants';
import { useMemo } from 'react';

export const LEAD_TIME_KEY_PREFIX = 'x_itemleadtime.';

/**
 * The stores a product carries an indexed lead time for. The search response only
 * projects the store it was queried with, so this doubles as the signal for which
 * store a payload belongs to.
 */
export const getLeadTimeStoreIds = (product: ProductType | undefined): string[] =>
	Object.keys(product ?? {})
		.filter((key) => key.startsWith(LEAD_TIME_KEY_PREFIX))
		.map((key) => key.slice(LEAD_TIME_KEY_PREFIX.length));

/**
 * LeadTime/Combos.md (updated): a combo's (TYPES.kit) delivery date is driven by
 * whichever component arrives soonest at the store — the smallest leadtime among
 * the components. Components with no leadtime for this store are ignored rather
 * than blocking the whole combo; if none of them carry one, the combo has none.
 */
const getComboLeadTime = (
	components: any[],
	physicalStoreId: string,
	requireLeadTimeRibbon: boolean
): number | undefined => {
	const componentLeadTimes = components
		.map((component) => getLeadTime(component, physicalStoreId, requireLeadTimeRibbon))
		.filter((leadTime): leadTime is number => leadTime !== undefined);
	return componentLeadTimes.length > 0 ? Math.min(...componentLeadTimes) : undefined;
};

/**
 * Extracts lead time value for a product based on its ribbons and indexed data.
 * Pure utility function that can be used inside or outside of React components.
 *
 * @param product The product object containing ribbons and custom fields.
 * @param physicalStoreId The physical store ID used to access store-specific lead time.
 * @param requireLeadTimeRibbon Defaults to `false`: PLP's EDD labels
 * @returns The lead time value as a number, or undefined if not applicable.
 */
// eslint-disable-next-line complexity
export const getLeadTime = (
	product: any,
	physicalStoreId: string,
	requireLeadTimeRibbon = false
): number | undefined => {
	if (!product || !physicalStoreId) return undefined;

	if (product.type === TYPES.kit) {
		return getComboLeadTime(product.components ?? [], physicalStoreId, requireLeadTimeRibbon);
	}

	if (requireLeadTimeRibbon) {
		const ribbons = product?.ribbons || [];
		const hasLeadTimeRibbon = ribbons.some(
			(ribbon: any) =>
				ribbon.identifier === PROMOTION_TYPES.PROMOICON_PC ||
				ribbon.identifier === PROMOTION_TYPES.PROMOICON_VF
		);
		if (!hasLeadTimeRibbon) return undefined;
	}

	// Always use x_itemleadtime, not the API call.
	const indexedLeadTime = product?.[`x_itemleadtime.${physicalStoreId}`];
	if (indexedLeadTime === undefined || indexedLeadTime === null || indexedLeadTime === '') {
		return undefined;
	}
	const numericLeadTime = Number(indexedLeadTime);
	return isNaN(numericLeadTime) ? undefined : numericLeadTime;
};

/**
 * Hook wrapper for getLeadTime.
 *
 * @param product The product object containing ribbons and custom fields.
 * @param physicalStoreId The physical store ID used to access store-specific lead time.
 * @param requireLeadTimeRibbon see `getLeadTime`.
 * @returns The lead time value as a number, or undefined if not applicable.
 */
export const useLeadTime = (
	product: any,
	physicalStoreId: string,
	requireLeadTimeRibbon = false
): number | undefined =>
	useMemo(
		() => getLeadTime(product, physicalStoreId, requireLeadTimeRibbon),
		[product, physicalStoreId, requireLeadTimeRibbon]
	);
